import * as React from "react";
import { useState, useEffect } from "react";
import {
  Title,
  Datagrid,
  TextField,
  DateField,
  BooleanField,
  List,
  ReferenceField,
  ReferenceArrayField,
  ChipField,
  SingleFieldList,
  TopToolbar,
  ExportButton,
  useNotify,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  MenuItem,
  Select,
} from "@mui/material";
import {
  BarChart,
  PieChart,
  ChartsTooltip,
  ChartsAxis,
  PieArcLabel,
} from "@mui/x-charts";
import {
  People as UsersIcon,
  CheckCircle as VerifiedUsersIcon,
  TrendingUp as GrowthIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Language as LocationIcon,
  Category as UserTypesIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField as MuiTextField } from "@mui/material";

import { UsersFilter, UsersList } from "../Users";

// Styled Summary Card with Icons
const SummaryCard = ({ title, value, secondaryValue, icon: Icon }) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        height: "100%",
        boxShadow: 3,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
        transition: "transform 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
        },
      }}
    >
      <CardContent sx={{ display: "flex", alignItems: "center" }}>
        <Box
          sx={{
            backgroundColor: theme.palette.primary.light,
            borderRadius: "50%",
            width: 56,
            height: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 2,
            color: theme.palette.primary.contrastText,
          }}
        >
          <Icon fontSize="medium" />
        </Box>
        <Box>
          <Typography variant="subtitle1" color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" component="div" fontWeight="bold">
            {value || 0}
          </Typography>
          {secondaryValue && (
            <Typography variant="body2" color="textSecondary">
              {secondaryValue}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const RecentUsersTable = ({ users }) => {
  const theme = useTheme();

  if (!Array.isArray(users) || users.length === 0) {
    return <Alert severity="info">No recent users found</Alert>;
  }

  // Sanitize and flatten user data
  const transformedUsers = users.map((user, index) => ({
    id: user.id || `fallback-id-${index}`,
    name: user.name || "N/A",
    phonenumber: user.phonenumber || "N/A",
    type: user.type || "N/A",
    gender: user.gender || "Not Set",
    stateName: user._state?.name || "Unknown",
    cityName: user._city?.name || "Unknown",
    created_at: user.created_at || new Date().toISOString(),
    isVerified: Boolean(user.isVerified),
  }));

  console.log("Transformed Users:", transformedUsers);

  return (
    <Card elevation={3}>
      <CardContent>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Recent Users
        </Typography>
        <Box sx={{ height: 500 }}>
          <Datagrid
            data={transformedUsers}
            bulkActionButtons={false}
            sx={{
              "& .RaDatagrid-headerCell": {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
                fontWeight: "bold",
              },
            }}
          >
            <TextField source="name" label="Name" />
            <TextField source="phonenumber" label="Phone" />
            <TextField source="type" label="Type" />
            <TextField source="gender" label="Gender" />
            <TextField source="stateName" label="State" />
            <TextField source="cityName" label="City" />
            <DateField source="created_at" label="Joined At" showTime />
            <BooleanField source="isVerified" label="Verified" />
          </Datagrid>
        </Box>
      </CardContent>
    </Card>
  );
};

const UsersReport = () => {
  const theme = useTheme();
  const notify = useNotify();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [gender, setGender] = useState("");
  const [startDate, setStartDate] = useState(""); // YYYY‑MM‑DD
  const [endDate, setEndDate] = useState("");
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");

  // Managers only see their assigned state(s); everyone else sees all states.
  // Fetch fresh on every mount rather than trusting a localStorage snapshot,
  // since an admin can change a manager's assignments at any time.
  const isManager = localStorage.getItem("permissions") === "manager";

  const genders = ["male", "female"];
  const types = [
    { id: "farmer", name: "Farmer" },
    { id: "agriculture_cooperative", name: "Farmer Cooperative" },
    { id: "consumer_cooperative", name: " Consumer Cooperative" },
    { id: "service_provider", name: "Service Provider" },
    { id: "buyer", name: "Buyer" },
  ];

  // Fetch the state list once: a manager gets only their assigned states,
  // everyone else gets the full list.
  useEffect(() => {
    const fetchStates = async () => {
      try {
        if (isManager) {
          const token = JSON.parse(localStorage.getItem("token"));
          const response = await fetch(
            `${process.env.REACT_APP_URL}/admin-state-assignments/me`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: "Bearer " + token,
              },
            }
          );
          const statesData = (await response.json()) || [];
          localStorage.setItem("states", JSON.stringify(statesData));
          setStates(statesData);
          if (statesData.length === 1) {
            setSelectedState(statesData[0].id);
          }
        } else {
          const stateResponse = await fetch(
            `${process.env.REACT_APP_URL}/admin/state`,
            {
              method: "GET",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
              },
            }
          );
          const statesData = await stateResponse.json();
          setStates(statesData);
        }
      } catch (err) {
        console.error("Error fetching states:", err);
        notify("Error loading states", { type: "warning" });
      }
    };
    fetchStates();
  }, [isManager, notify]);

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      try {
        const token = JSON.parse(localStorage.getItem("token"));
        const params = new URLSearchParams();
        if (typeFilter) params.append("type", typeFilter);
        if (gender) params.append("gender", gender);
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        if (selectedState) {
          params.append("state", selectedState);
        } else if (isManager && states.length > 0) {
          // Manager has more than one assigned state and hasn't narrowed to a
          // single one — scope the request to all states they're assigned to.
          params.append(
            "states",
            states.map((s) => s.id).join(",")
          );
        }

        const url =
          `${process.env.REACT_APP_URL}/admin/reports/users` +
          (params.toString() ? `?${params.toString()}` : "");
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch report data");

        const result = await response.json();
        const data = result.data || {};

        // Transformed object-based structure
        const transformedData = {
          summary: data.summary || {
            totalUsers: 0,
            verifiedUsers: 0,
            activePercentage: 0,
          },
          growth: {
            monthly: data.growth?.monthly || [],
            total: data.growth?.total || 0,
          },
          demographics: {
            gender: data.demographics?.gender || [],
            topLocations: data.demographics?.topLocations || [],
          },
          userTypes: Array.isArray(data.userTypes) ? data.userTypes : [],
          recentUsers: Array.isArray(data.recentUsers) ? data.recentUsers : [],
          usersByState: Array.isArray(data.usersByState)
            ? data.usersByState
            : [],
        };
        setReportData(transformedData);
      } catch (err) {
        console.error("Error fetching report:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (states.length > 0) {
      fetchReportData();
    }
  }, [typeFilter, gender, startDate, endDate, selectedState, isManager, states]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="300px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        Failed to load user report data. Please try again later.
        <Box mt={1} fontFamily="monospace">
          {error}
        </Box>
      </Alert>
    );
  }

  if (!reportData) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        No user report data available.
      </Alert>
    );
  }

  const {
    summary,
    growth,
    demographics,
    userTypes,
    recentUsers,
    usersByState,
  } = reportData;

  // Prepare chart data with fallbacks
  const growthChartData = growth.monthly.map((item) => ({
    month: `${item.month} ${item.year}`,
    count: item.count || 0,
  }));

  const genderChartData = demographics.gender.map((item) => ({
    id: item.gender || "unknown",
    label: item.gender
      ? item.gender.charAt(0).toUpperCase() + item.gender.slice(1)
      : "Not Set",
    value: parseInt(item.count) || 0,
    icon: item.gender === "male" ? <MaleIcon /> : <FemaleIcon />,
  }));

  const userTypesChartData = userTypes.map((item) => ({
    id: item.type,
    label: item.type.charAt(0).toUpperCase() + item.type.slice(1),
    value: parseInt(item.count) || 0,
  }));

  const locationsChartData = usersByState.map((item) => ({
    id: item.stateid,
    label: item.statename,
    value: parseInt(item.usercount) || 0,
  }));

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.background.default,
          0.9
        )} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
        p: 3,
        borderRadius: "16px",
      }}
    >
      <Title title="User Reports" />
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          fontWeight="800"
          sx={{
            background: `black`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          User Analytics Dashboard
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Total Users"
            value={summary.totalUsers}
            icon={UsersIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Verified Users"
            value={summary.verifiedUsers}
            secondaryValue={`${
              summary.activePercentage?.toFixed(2) || 0
            }% of total`}
            icon={VerifiedUsersIcon}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="New This Month"
            value={growth.monthly[growth.monthly.length - 1]?.count || 0}
            icon={GrowthIcon}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Paper elevation={2}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Analytics Overview" />
            <Tab label="Recent Users" />
          </Tabs>
        </Paper>
      </Box>

      {/* Overview Tab */}
      {activeTab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">Gender</Typography>
              <Select
                fullWidth
                value={gender}
                onChange={(e) => setGender(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">Any</MenuItem>
                {genders.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">Type</Typography>
              <Select
                fullWidth
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">Any</MenuItem>
                {types.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="subtitle2">State</Typography>
              <Select
                fullWidth
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
                displayEmpty
                size="small"
              >
                <MenuItem value="">All</MenuItem>
                {states.map((state) => (
                  <MenuItem key={state.id} value={state.id}>
                    {state.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={6} sm={3} md={2}>
              <DatePicker
                label="From"
                value={startDate || null}
                onChange={(newDate) => {
                  // normalize to YYYY-MM-DD string or empty
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setStartDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <DatePicker
                label="To"
                value={endDate || null}
                onChange={(newDate) => {
                  const iso = newDate ? newDate.toISOString().slice(0, 10) : "";
                  setEndDate(iso);
                }}
                renderInput={(params) => (
                  <MuiTextField
                    {...params}
                    fullWidth
                    size="small"
                    inputProps={{ ...params.inputProps, readOnly: true }}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* User Growth Chart */}
            <Grid item xs={12} lg={6}>
              <Card elevation={3} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Monthly User Growth
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {Array.isArray(growthChartData) &&
                    growthChartData.length > 0 ? (
                      <BarChart
                        dataset={growthChartData}
                        xAxis={[
                          {
                            scaleType: "band",
                            dataKey: "month",
                            label: "Month",
                          },
                        ]}
                        series={[
                          {
                            dataKey: "count",
                            label: "New Users",
                            color: theme.palette.primary.main,
                          },
                        ]}
                        yAxis={[
                          {
                            label: "Number of Users",
                          },
                        ]}
                      >
                        <ChartsAxis />
                        <ChartsTooltip />
                      </BarChart>
                    ) : (
                      <Alert severity="info">
                        No data available for this chart
                      </Alert>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Gender Distribution */}
            <Grid item xs={12} lg={6}>
              <Card elevation={3} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Gender Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <PieChart
                      series={[
                        {
                          data: genderChartData,
                          innerRadius: 40,
                          outerRadius: 100,
                          paddingAngle: 5,
                          cornerRadius: 5,
                          highlightScope: {
                            faded: "global",
                            highlighted: "item",
                          },
                          faded: {
                            innerRadius: 30,
                            additionalRadius: -30,
                            color: "gray",
                          },
                        },
                      ]}
                    >
                      <ChartsTooltip />
                      <PieArcLabel />
                    </PieChart>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* User Types */}
            <Grid item xs={12} lg={6}>
              <Card elevation={3} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    User Types Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <PieChart
                      series={[
                        {
                          data: userTypesChartData,
                          innerRadius: 40,
                          outerRadius: 100,
                          paddingAngle: 5,
                          cornerRadius: 5,
                          highlightScope: {
                            faded: "global",
                            highlighted: "item",
                          },
                          faded: {
                            innerRadius: 30,
                            additionalRadius: -30,
                            color: "gray",
                          },
                        },
                      ]}
                    >
                      <ChartsTooltip />
                      <PieArcLabel />
                    </PieChart>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Locations */}
            <Grid item xs={12} lg={6}>
              <Card elevation={3} sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    Users by Location
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {Array.isArray(locationsChartData) &&
                    locationsChartData.length > 0 ? (
                      <BarChart
                        dataset={locationsChartData}
                        xAxis={[
                          {
                            scaleType: "band",
                            dataKey: "label",
                            label: "State",
                          },
                        ]}
                        series={[
                          {
                            dataKey: "value",
                            label: "Users",
                            color: theme.palette.primary.main,
                          },
                        ]}
                        yAxis={[
                          {
                            label: "Number of Users",
                          },
                        ]}
                      >
                        <ChartsAxis />
                        <ChartsTooltip />
                      </BarChart>
                    ) : (
                      <Alert severity="info">
                        No data available for this chart
                      </Alert>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {/* Recent Users Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <UsersList
            resource="users"
            permanentFilter={
              isManager && states.length === 1
                ? { location: states[0].id }
                : isManager && states.length > 1
                ? { "location||$in": states.map((s) => s.id) }
                : {}
            }
          />
        </Grid>
        // <Grid container spacing={3}>
        //   <Grid item xs={12}>
        //     <List resource="users" filters={<UsersFilter />} actions={<ExportListAction />}>
        //       <Datagrid className="mt-4">
        //         <TextField source="name" />
        //         <TextField source="phonenumber" />
        //         <TextField source="type" />
        //         <TextField source="gender" />
        //         <DateField source="created_at" />
        //         <ReferenceField source="location" reference="state">
        //           <TextField source="name" />
        //         </ReferenceField>
        //         <ReferenceArrayField
        //           reference="servicetypes"
        //           source="service_id"
        //         >
        //           <SingleFieldList>
        //             <ChipField source="name" />
        //           </SingleFieldList>
        //         </ReferenceArrayField>
        //         <ReferenceArrayField
        //           reference="sub_category"
        //           source="subCategory_id"
        //         >
        //           <SingleFieldList>
        //             <ChipField source="name" />
        //           </SingleFieldList>
        //         </ReferenceArrayField>
        //         <DateField source="dob" />
        //       </Datagrid>
        //     </List>
        //   </Grid>
        // </Grid>
      )}
    </Box>
  );
};

export default UsersReport;

export const ExportListAction = () => (
  <TopToolbar>
    <ExportButton />
  </TopToolbar>
);
